import { bindingBehavior } from 'aurelia-binding';
import { Optional } from 'aurelia-dependency-injection';
import { DOM, TaskQueue } from 'aurelia-framework';
import { validateTrigger, ValidationController } from 'aurelia-validation';
import { getTargetDOMElement } from './get-target-dom-element';

export interface ValidateCustomElementBindingBehaviorOptions {
	validationController?: ValidationController;
	elementSelector?: string;
	rules?: any;
	onBlurValidationDelayerProvider?: () => PromiseLike<any>;
}

/**
 * This is modification version of aurelia validation validate-binding-behavior-base.ts
 *
 * Original ValidateBindingBehaviorBase issues:
 * 1. When used on custom element's attribute, getTargetDOMElement gets this custom element dom. Since this dom may
 * not have a blur event. You are required to dispatch event yourself. An example
 * ```javascript
 * @autoinject
 * export class MyCustomComponent {
 *   @bindable({
 *     defaultBindingMode: bindingMode.twoWay,
 *     changeHandler: 'valueChanged'})
 *   public value: any;
 *   constructor(private readonly element: Element) {}
 *   attached() {
 *     elemet.querySelector('#field-input').addEventListener(
 *       'blur',
 *       this.element.dispatchEvent(new Event('blur');
 *   }
 * }
 * ```
 * ```html
 * <template>
 *   <my-custom-component
 *     value.bind="value & validate"
 *   >
 *   </my-custom-component>
 * </template>
 * ```
 *
 * 2. When used on custom element's attribute which is containerless, getTargetDOMElement gets a `comment` element.
 * Is it intentional or a bug? So this modifined ValidateCustomElementBindingBehavior change getTargetDOMElement
 * behavior. If the binding target is containerless, try to find target that match elementSelector from siblings
 * and their children.
 *
 * ValidateCustomElementBindingBehavior:
 * 1. Can work as ValidateBindingBehaviorBase
 * 2. Can accept a elementSelector parameter. This is useful when this behavior is used on custom element by blur
 * trigger, can pass in the selector of real dom element which actually dispatch the blur event
 * 3. Fix containerless custom element bug. I hope it works >_<
 *
 * See search.html as example
 *
 */
@bindingBehavior('validateCustomElement')
export class ValidateCustomElementBindingBehavior {
	public static inject = [TaskQueue];

	constructor(private taskQueue: TaskQueue) {}

	public getValidateTrigger(controller) {
		return validateTrigger.changeOrBlur;
	}

	/**
	 * Attach the validation behaviour to an Aurelia binding that is defined within a view object.
	 * The actual validation that occurs on a blur event can be delayed until a promise is resolved.
	 * This is used for <a-search> so that its value is only validated after a search suggestion is chosen.
	 * @param binding The binding
	 * @param source The view object
	 * @param {ValidateCustomElementBindingBehaviorOptions} options options.onBlurValidationDelayerProvider provides the
	 * promise to wait for every time the binding is activated by a blur event.
	 */
	public async bind(
		binding: any,
		source: any,
		options: ValidateCustomElementBindingBehaviorOptions,
	) {
		let elementSelector: string;

		if (typeof options.elementSelector === 'string') {
			elementSelector = options.elementSelector;
		}

		const target = await getTargetDOMElement(
			binding,
			source,
			elementSelector,
		);
		if (!target) {
			return;
		}

		// locate the controller.
		let controller: ValidationController;
		if (options.validationController instanceof ValidationController) {
			controller = options.validationController;
		} else {
			controller = source.container.get(
				Optional.of(ValidationController),
			);
		}
		if (controller === null) {
			throw new Error(`A ValidationController has not been registered.`);
		}

		const rules = options.rules;

		const onBlurValidationDelayerProvider =
			options.onBlurValidationDelayerProvider
				? options.onBlurValidationDelayerProvider
				: () => Promise.resolve();

		controller.registerBinding(binding, target, rules);
		binding.validationController = controller;
		const trigger = this.getValidateTrigger(controller);
		// eslint-disable-next-line no-bitwise
		if (trigger & validateTrigger.change) {
			binding.vbbUpdateSource = binding.updateSource;
			// tslint:disable-next-line:only-arrow-functions
			// tslint:disable-next-line:space-before-function-paren
			binding.updateSource = function (value: any) {
				this.vbbUpdateSource(value);
				this.validationController.validateBinding(this);
			};
		}

		// eslint-disable-next-line no-bitwise
		if (trigger & validateTrigger.blur) {
			binding.validateBlurHandler = () => {
				this.taskQueue.queueMicroTask(() => {
					const onBlurValidationDelayer: PromiseLike<any> =
						onBlurValidationDelayerProvider();

					onBlurValidationDelayer.then(() => {
						controller.validateBinding(binding);
					});
				});
			};
			binding.validateTarget = target;
			target.addEventListener('blur', binding.validateBlurHandler);
		}

		if (trigger !== validateTrigger.manual) {
			binding.standardUpdateTarget = binding.updateTarget;
			// tslint:disable-next-line:only-arrow-functions
			// tslint:disable-next-line:space-before-function-paren
			binding.updateTarget = function (value: any) {
				this.standardUpdateTarget(value);
				this.validationController.resetBinding(this);
			};
		}
	}

	public unbind(binding: any) {
		// reset the binding to it's original state.
		if (binding.vbbUpdateSource) {
			binding.updateSource = binding.vbbUpdateSource;
			binding.vbbUpdateSource = null;
		}
		if (binding.standardUpdateTarget) {
			binding.updateTarget = binding.standardUpdateTarget;
			binding.standardUpdateTarget = null;
		}
		if (binding.validateBlurHandler) {
			binding.validateTarget.removeEventListener(
				'blur',
				binding.validateBlurHandler,
			);
			binding.validateBlurHandler = null;
			binding.validateTarget = null;
		}
		if (binding.validationController) {
			binding.validationController.unregisterBinding(binding);
			binding.validationController = null;
		}
	}
}
